"use client"

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Mic, Square, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

interface VoiceRecordingModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: (audioBlob: Blob) => void
    autoStart?: boolean // Auto-start recording when modal opens
    imageUrl?: string // Optional thumbnail image URL
}

/**
 * Voice recording modal with waveform visualization
 * Uses MediaRecorder API to record audio and visualize with a simple waveform
 */
export default function VoiceRecordingModal({
    isOpen,
    onClose,
    onSave,
    autoStart = false,
    imageUrl,
}: VoiceRecordingModalProps) {
    const [isRecording, setIsRecording] = useState(false)
    const [recordingTime, setRecordingTime] = useState(0)
    const [audioLevel, setAudioLevel] = useState(0) // For waveform visualization
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioChunksRef = useRef<Blob[]>([])
    const streamRef = useRef<MediaStream | null>(null)
    const audioContextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const animationFrameRef = useRef<number | null>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const cleanup = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
        }
        if (audioContextRef.current) {
            audioContextRef.current.close()
            audioContextRef.current = null
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }
        if (timerRef.current) {
            clearInterval(timerRef.current)
        }
        analyserRef.current = null
        setRecordingTime(0)
        setAudioLevel(0)
    }, [])

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop()
            setIsRecording(false)
        }
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }
    }, []) // Remove isRecording dependency - check state directly instead

    useEffect(() => {
        if (!isOpen) {
            stopRecording()
            cleanup()
        } else if (autoStart && !isRecording) {
            // Auto-start recording when modal opens if autoStart is true
            startRecording()
        }
        return () => {
            stopRecording()
            cleanup()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, autoStart])

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            streamRef.current = stream

            // Setup audio context for visualization
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
            audioContextRef.current = audioContext

            const analyser = audioContext.createAnalyser()
            analyser.fftSize = 256
            analyserRef.current = analyser

            const source = audioContext.createMediaStreamSource(stream)
            source.connect(analyser)

            // Use audio/webm;codecs=opus - most efficient format supported by browsers and AWS Transcribe
            const mimeType = 'audio/webm;codecs=opus'
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : undefined,
            })
            mediaRecorderRef.current = mediaRecorder
            audioChunksRef.current = []

            const actualMimeType = mediaRecorder.mimeType || 'audio/webm'
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data)
                }
            }

            mediaRecorder.onstop = () => {
                if (audioChunksRef.current.length === 0) {
                    alert('No audio data was recorded. Please try again.')
                    return
                }
                
                const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType })
                onSave(audioBlob)
            }

            mediaRecorder.start()
            setIsRecording(true)
            setRecordingTime(0)

            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1)
            }, 1000)

            // Start waveform visualization
            visualizeWaveform()
        } catch (error) {
            console.error('Error starting recording:', error)
            alert('Unable to access microphone. Please check permissions.')
        }
    }

    const visualizeWaveform = () => {
        if (!analyserRef.current) return

        const analyser = analyserRef.current
        const timeDataArray = new Uint8Array(analyser.frequencyBinCount)

        const updateVisualization = () => {
            if (!analyserRef.current || !isRecording) {
                animationFrameRef.current = null
                return
            }

            // Use getByteTimeDomainData for better voice visualization (waveform)
            analyser.getByteTimeDomainData(timeDataArray)
            // Calculate RMS (Root Mean Square) for better audio level representation
            let sum = 0
            for (let i = 0; i < timeDataArray.length; i++) {
                const normalized = (timeDataArray[i] - 128) / 128
                sum += normalized * normalized
            }
            const rms = Math.sqrt(sum / timeDataArray.length)
            setAudioLevel(Math.min(1, rms * 5)) // Amplify and cap at 1 (increased multiplier for better visibility)

            animationFrameRef.current = requestAnimationFrame(updateVisualization)
        }

        updateVisualization()
    }

    const handleSave = () => {
        stopRecording()
        // The onstop handler will call onSave
    }

    const handleCancel = () => {
        stopRecording()
        cleanup()
        audioChunksRef.current = []
        onClose()
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            handleCancel()
        }
    }

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Generate waveform bars based on audio level
    const waveformBars = Array.from({ length: 20 }, (_, i) => {
        const baseHeight = audioLevel * 100
        const variation = Math.sin((i / 20) * Math.PI * 2) * 20
        return Math.max(10, baseHeight + variation)
    })

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            {/* Above the photo viewer overlay (z-[100]) so it is usable from the viewer */}
            <DialogContent className="z-[110] sm:max-w-md" hideCloseButton>
                <DialogTitle className="sr-only">Record Description</DialogTitle>
                <DialogDescription className="sr-only">Record a voice description for the photo</DialogDescription>
                <div className="flex flex-col items-center gap-6 p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between w-full">
                        <h2 className="text-lg font-semibold">Record Description</h2>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleCancel}
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Photo thumbnail */}
                    {imageUrl && (
                        <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={imageUrl}
                                alt="Photo to describe"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Waveform visualization - smaller */}
                    <div className="flex items-end justify-center gap-0.5 h-12 w-full px-4">
                        {waveformBars.map((height, index) => (
                            <div
                                key={index}
                                className="bg-primary rounded-t flex-1 transition-all duration-75"
                                style={{
                                    height: `${height}%`,
                                    minHeight: '2px',
                                }}
                            />
                        ))}
                    </div>

                    {/* Recording time */}
                    <div className="text-2xl font-mono font-semibold">
                        {formatTime(recordingTime)}
                    </div>

                    {/* Recording indicator */}
                    {isRecording && (
                        <div className="flex items-center gap-2 text-red-600">
                            <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
                            <span className="text-sm font-medium">Recording</span>
                        </div>
                    )}

                    {/* Action buttons - only show when recording (auto-start will start immediately) */}
                    {isRecording && (
                        <div className="flex gap-4 w-full">
                            <Button
                                onClick={handleCancel}
                                variant="outline"
                                size="lg"
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                size="lg"
                                className="flex-1 flex items-center justify-center gap-2 bg-primary"
                            >
                                <Square className="w-5 h-5" />
                                Done
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

